import { message } from "antd";
import axios from "axios";
import * as CryptoJS from 'crypto-js';
import {
  getApiResponseErrorMessage,
  getApiResponseStatusCode,
  isFeatureComingSoonMessage,
  isPermissionDeniedMessage,
  isSuccessfulApiResponse,
  NETWORK_ERROR_MESSAGE,
  REQUEST_TIMEOUT_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
} from "../common/apiResponse";
import { handleForbiddenApiAccess, handleUnauthorizedSession } from "./forbiddenNavigation";

export const API_URL=process.env.REACT_APP_API_URL_REFACTOR;
// export const API_URL = "http://192.168.9.172:8080"

export function createPayloadKeys(encryptionKey) {
  let mdString = CryptoJS.MD5(encryptionKey).toString();
  let key = CryptoJS.enc.Utf8.parse(mdString.substring(0, 16));
  let iv = CryptoJS.enc.Utf8.parse(mdString.substring(16));
  return { key, iv };
}

// Function to encrypt payload
export const encryptPayload = (textToEncrypt, keyData) => {
  let encrypted = CryptoJS.AES.encrypt(textToEncrypt, keyData.key, {
    iv: keyData.iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  return encrypted.toString();
}

// Function to decrypt payload
export const decryptPayload = (encryptedText, keyData) => {
  let decrypted = CryptoJS.AES.decrypt(encryptedText, keyData.key, {
    iv: keyData.iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
}

const getApiErrorMessage = (error, keyData) => {
  try {
    if (error?.response?.data?.body) {
      const decryptedResponse = decryptPayload(error.response.data.body, keyData);
      const parsed = JSON.parse(decryptedResponse);
      return parsed.message || parsed.error || null;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
  } catch (parseError) {
    return null;
  }

  return null;
};

const API_ERROR_MESSAGE_KEY = "global-api-error";
let apiErrorCooldownUntil = 0;
const API_ERROR_COOLDOWN_MS = 2500;

export { API_ERROR_MESSAGE_KEY };

export const canShowApiError = () => Date.now() >= apiErrorCooldownUntil;

export const markApiErrorShown = () => {
  apiErrorCooldownUntil = Date.now() + API_ERROR_COOLDOWN_MS;
};

export const showApiError = (errorMessage) => {
  if (
    !errorMessage ||
    !canShowApiError() ||
    isPermissionDeniedMessage(errorMessage) ||
    isFeatureComingSoonMessage(errorMessage)
  ) {
    return false;
  }

  markApiErrorShown();
  message.error({
    content: errorMessage,
    key: API_ERROR_MESSAGE_KEY,
  });
  return true;
};

// HTTP client class
export default class Http {
  static post(url, body) {
    return new Promise((resolve, reject) => {
      // Generate encryption keys
      const { key, iv } = createPayloadKeys(process.env.REACT_APP_ENCRYPTION_KEY);
      // Encrypt the payload
      const encryptedBody = encryptPayload(JSON.stringify(body), { key, iv });

      // Make API request
      axios.post(`${API_URL}/${url}`, { body: encryptedBody }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : {}
        },
        timeout: 60000
      })
        .then(response => { 
          const decryptedResponse = decryptPayload(response.data.body, { key, iv });
          const parsed = JSON.parse(decryptedResponse);

          if (!isSuccessfulApiResponse(parsed)) {
            const statusCode = getApiResponseStatusCode(parsed);
            const errorMessage =
              getApiResponseErrorMessage(parsed) || "Something went wrong.";

            if (statusCode === 401) {
              handleUnauthorizedSession(
                isPermissionDeniedMessage(errorMessage)
                  ? SESSION_EXPIRED_MESSAGE
                  : errorMessage,
              );
            } else if (statusCode === 405) {
              handleForbiddenApiAccess(errorMessage);
            }

            reject(errorMessage);
            return;
          }

          resolve(parsed);
        })
        .catch(error => {
          const keyData = { key, iv };

          if (error.code === "ECONNABORTED") {
            reject(REQUEST_TIMEOUT_MESSAGE);
            showApiError(REQUEST_TIMEOUT_MESSAGE);
            return;
          }

          if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
            reject(NETWORK_ERROR_MESSAGE);
            showApiError(NETWORK_ERROR_MESSAGE);
            return;
          }

          if (error?.response?.status === 401) {
            const errorMessage =
              getApiErrorMessage(error, keyData) || SESSION_EXPIRED_MESSAGE;
            handleUnauthorizedSession(
              isPermissionDeniedMessage(errorMessage)
                ? SESSION_EXPIRED_MESSAGE
                : errorMessage,
            );
            reject(errorMessage);
            return;
          }

          if (error?.response?.status === 405) {
            const errorMessage =
              getApiErrorMessage(error, keyData) || "Something went wrong.";
            handleForbiddenApiAccess(errorMessage);
            reject(errorMessage);
            return;
          }

          const errorMessage =
            getApiErrorMessage(error, keyData) || "Something went wrong.";

          if (!isPermissionDeniedMessage(errorMessage) && !isFeatureComingSoonMessage(errorMessage)) {
            showApiError(errorMessage);
          }

          reject(errorMessage);
        });
    });
  }
}
